import { Service } from "models/Service";
import {
  buildArmaTuEquipoEditorIframeUrl,
  buildArmaTuEquipoLineupViewerUrl,
  buildArmaTuEquipoPublicPagePathWithUserInQuery,
} from "@/helpers/armatuEquipoUrls";

const defaultIframeBase = (() => {
  const raw = process.env.NEXT_PUBLIC_FRONT_HANDLER_URL || "https://sd-front.clarin.com";
  return `${String(raw).replace(/\/$/, "")}/`;
})();

export function buildArmaTuEquipoIframeUrl(teamSlug) {
  return buildArmaTuEquipoEditorIframeUrl(defaultIframeBase, teamSlug, "clarin");
}

/** Varias rutas en `match` unidas (sin prefijos ecuador/usa — solo Clarín AR). */
export function parseArmaTuEquipoTeamSlug(matchSegments) {
  if (!matchSegments?.length) return null;
  const joined = matchSegments
    .map((s) => (s ?? "").split(".")[0])
    .filter(Boolean)
    .join("/");
  if (!joined.startsWith("arma-tu-equipo-")) return null;
  const rest = joined.slice("arma-tu-equipo-".length);
  const teamSlug = rest.split("/")[0];
  return teamSlug || null;
}

export function parseArmaTuEquipoPathUserId(matchSegments) {
  if (!matchSegments?.length) return null;
  const joined = matchSegments
    .map((s) => (s ?? "").split(".")[0])
    .filter(Boolean)
    .join("/");
  if (!joined.startsWith("arma-tu-equipo-")) return null;
  const rest = joined.slice("arma-tu-equipo-".length);
  const parts = rest.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const raw = parts[1];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function pickQueryParam(fullQuery, key) {
  if (!fullQuery || fullQuery[key] == null) return null;
  const v = fullQuery[key];
  return Array.isArray(v) ? v[0] : v;
}

function userIdFromQuery(fullQuery) {
  const raw = pickQueryParam(fullQuery, "userId");
  if (raw == null || raw === "") return null;
  try {
    return decodeURIComponent(String(raw));
  } catch {
    return String(raw);
  }
}

function apiHandlerBaseForSeleccion() {
  const fromOverride = String(
    process.env.ARMA_TU_EQUIPO_API_HANDLER_URL || "",
  )
    .trim()
    .replace(/\/$/, "");
  if (fromOverride) return fromOverride;
  return String(process.env.API_HANDLER_BACKEND_URL || "")
    .trim()
    .replace(/\/$/, "");
}

/** GET seleccion-11 en SSR: mismo criterio que el iframe. */
async function fetchSeleccion11DocId({ product, userId, team, matchId }) {
  const base = apiHandlerBaseForSeleccion();
  const uid = String(userId || "").trim();
  const tm = String(team || "").trim();
  if (!base || !uid || !tm) return null;
  const qs = new URLSearchParams({ userId: uid, team: tm });
  const mid = String(matchId || "").trim();
  if (mid) qs.set("matchId", mid);
  const url = `${base}/api/arma-tu-equipo/${encodeURIComponent(String(product || "clarin").trim())}/seleccion-11?${qs.toString()}`;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 8000);
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!res.ok) return null;
    const json = await res.json().catch(() => null);
    if (!json || Number(json.code) !== 200) return null;
    const id = json.docId != null ? String(json.docId).trim() : "";
    return id || null;
  } catch {
    return null;
  }
}

function formatTeamName(teamSlug) {
  if (!teamSlug) return "";
  return teamSlug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export class BuildYourTeam extends Service {
  constructor() {
    super({
      adsPath: "mundialfutbol-2026/home",
      meta: {
        description: "Armá tu equipo ideal y compartilo con tus amigos - Clarin.com",
        keywords: "arma tu equipo, futbol, equipos, clarin, ole",
        title: "Armá tu equipo - Clarin.com",
        url: "/arma-tu-equipo",
        extra: [],
      },
      section: "Armá tu equipo",
      sectionAds: "armatuequipo",
      sectionName: "Armá tu equipo",
      template: "BuildYourTeam",
      title: "Armá tu equipo",
      slug: "arma-tu-equipo",
      autorefresh: false,
      widthFull: true,
      bannersOn: true,
      bannerHorizontalOff: ["horizontal3"],
    });
  }

  parseData = async (matchSegments, menu, fullQuery = {}) => {
    const teamSlug = parseArmaTuEquipoTeamSlug(matchSegments);
    const pathUserIdFromPath = parseArmaTuEquipoPathUserId(matchSegments);
    const userIdFromQ = userIdFromQuery(fullQuery);
    const resolvedUserId = pathUserIdFromPath ?? userIdFromQ;
    const teamName = formatTeamName(teamSlug);
    const teamQ = pickQueryParam(fullQuery, "team") ?? null;
    const iframeUrl =
      teamSlug && resolvedUserId
        ? buildArmaTuEquipoLineupViewerUrl(
            defaultIframeBase,
            teamSlug,
            resolvedUserId,
            "clarin",
          ) || buildArmaTuEquipoIframeUrl(teamSlug)
        : teamSlug
          ? buildArmaTuEquipoIframeUrl(teamSlug)
          : "";
    const matchIdFromQuery = pickQueryParam(fullQuery, "matchId");
    const matchIdNorm =
      matchIdFromQuery != null && String(matchIdFromQuery).trim() !== ""
        ? String(matchIdFromQuery).trim()
        : "";
    const routeSlug =
      resolvedUserId && teamSlug
        ? buildArmaTuEquipoPublicPagePathWithUserInQuery(
            teamSlug,
            resolvedUserId,
            teamQ ?? teamSlug,
            null,
            matchIdNorm || null,
          )
        : teamSlug
          ? `arma-tu-equipo-${teamSlug}`
          : "arma-tu-equipo";
    const pageTitle = teamName
      ? `Armá tu equipo de ${teamName} - Clarin.com`
      : "Armá tu equipo - Clarin.com";
    const pageDescription = teamName
      ? `Armá tu equipo ideal de ${teamName} y compartilo con tus amigos - Clarin.com`
      : "Armá tu equipo ideal y compartilo con tus amigos - Clarin.com";

    this.setTitle(pageTitle);
    this.setSection(teamName ? `Armá tu equipo de ${teamName}` : "Armá tu equipo");
    this.setMeta(
      this.parseMeta({
        description: pageDescription,
        keywords: `arma tu equipo, ${teamName}, futbol, clarin, ole`,
        title: pageTitle,
        url: routeSlug,
        extra: [],
      })
    );
    const userIdQ = pickQueryParam(fullQuery, "userId") ?? null;
    const docIdFromQuery = pickQueryParam(fullQuery, "docId");
    const teamForSeleccionApi = String(teamQ ?? teamSlug ?? "").trim();
    let docId =
      docIdFromQuery != null && String(docIdFromQuery).trim() !== ""
        ? String(docIdFromQuery).trim()
        : "";
    if (
      !docId &&
      resolvedUserId &&
      teamSlug &&
      teamForSeleccionApi
    ) {
      const fromApi = await fetchSeleccion11DocId({
        product: "clarin",
        userId: resolvedUserId,
        team: teamForSeleccionApi,
        matchId: matchIdNorm || undefined,
      });
      if (fromApi) docId = fromApi;
    }

    this.setData({
      teamSlug,
      teamName,
      iframeUrl,
      pathUserId: resolvedUserId ?? null,
      team: teamQ ?? teamSlug ?? null,
      urlUserId: userIdQ,
      docId: docId || null,
      matchId: matchIdNorm || null,
    });
  };
}
